import { Facebook, Instagram, Mail, MapPin, PartyPopper, Phone, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { ChapterHeading, RainbowMark, Reveal } from "./Reveal";

const EMAIL = "nbenachour@milestonepediatrictherapyllc.com";

export const Contact = () => (
  <>
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-white/60 border-t border-brand-lightblue/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeading
          number="07"
          eyebrow="Contact Us"
          title="We would love"
          accent="to connect."
          testid="contact-heading"
        />

        <div className="grid md:grid-cols-2 gap-8">
          <Reveal>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-card"
              className="group block h-full rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft p-10 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-lift hover:border-brand-coral/40"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-pale text-brand-blue mb-8 transition-transform duration-500 group-hover:scale-110">
                <Mail className="h-7 w-7" />
              </span>
              <h3 className="font-serif text-3xl font-semibold text-brand-blue mb-3">Write to us</h3>
              <p className="text-brand-muted text-sm md:text-base leading-relaxed mb-6">
                Families, providers, community partners, and prospective team members are welcome to
                reach out as we prepare to open.
              </p>
              <span className="text-brand-coral font-semibold break-all" data-testid="contact-email-address">
                {EMAIL}
              </span>
              <span className="mt-4 flex items-center gap-2.5" data-testid="contact-phone-number">
                <Phone className="h-4 w-4 text-brand-coral shrink-0" />
                <a
                  href="tel:+18325919535"
                  className="text-brand-blue font-semibold hover:text-brand-coral transition-colors duration-300"
                >
                  (832) 591-9535
                </a>
              </span>
              <span className="mt-3 flex items-center gap-2.5" data-testid="contact-fax-number">
                <Printer className="h-4 w-4 text-brand-coral shrink-0" />
                <span className="text-brand-blue font-semibold">
                  Fax: +1 (832) 201-0421
                </span>
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="contact-opening-card"
              className="h-full rounded-[2rem] bg-brand-blue text-white shadow-soft p-10 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-lift"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-brand-yellow mb-8">
                <PartyPopper className="h-7 w-7" />
              </span>
              <h3 className="font-serif text-3xl font-semibold mb-3">Opening Soon</h3>
              <p className="text-white/85 text-sm md:text-base leading-relaxed">
                We look forward to welcoming community partners to our grand opening and clinic
                tours once the facility is ready.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="md:col-span-2">
            <a
              href="https://maps.google.com/?q=306+West+Edgewood+Suite+D,+Friendswood,+TX+77546"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address-card"
              className="group flex flex-wrap items-center gap-6 rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft p-8 md:p-10 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-lift hover:border-brand-coral/40"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-pale text-brand-blue transition-transform duration-500 group-hover:scale-110 shrink-0">
                <MapPin className="h-7 w-7" />
              </span>
              <span className="flex-1 min-w-56">
                <span className="block font-serif text-2xl md:text-3xl font-semibold text-brand-blue mb-1">Visit us</span>
                <span className="block text-brand-muted text-sm md:text-base" data-testid="contact-address">
                  306 West Edgewood, Suite D · Friendswood, TX 77546
                </span>
              </span>
              <span className="inline-flex items-center gap-2 text-brand-coral font-semibold text-sm">
                Get Directions
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </span>
            </a>
          </Reveal>
        </div>
      </div>
    </section>

    <Footer />
  </>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-deep text-white overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <Reveal>
          <p className="font-serif italic font-light text-5xl sm:text-6xl lg:text-8xl leading-none text-white/95" data-testid="footer-big-text">
            Opening Soon<span className="text-brand-coral not-italic">.</span>
          </p>
        </Reveal>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <RainbowMark className="h-7 w-11" />
            <span className="leading-none">
              <span className="block font-serif text-lg font-semibold">Milestone</span>
              <span className="block text-[10px] uppercase tracking-[0.3em] text-brand-yellow font-semibold mt-0.5">
                Pediatric Therapy
              </span>
            </span>
          </div>
          <div className="flex flex-col gap-1.5 text-sm text-white/80">
            <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="hover:text-white transition-colors duration-300 break-all">
              {EMAIL}
            </a>
            <a href="tel:+18325919535" data-testid="footer-phone" className="hover:text-white transition-colors duration-300">
              (832) 591-9535
            </a>
            <span className="text-white/60" data-testid="footer-fax">
              Fax: +1 (832) 201-0421
            </span>
            <span className="text-white/60" data-testid="footer-address">
              306 West Edgewood, Suite D · Friendswood, TX 77546
            </span>
          </div>
          <div className="flex items-center gap-3" data-testid="footer-social">
            <a href="https://instagram.com/milestonepediatric" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-brand-coral hover:text-white transition-[background-color,color] duration-300">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="https://facebook.com/Milestonepediatric" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-brand-coral hover:text-white transition-[background-color,color] duration-300">
              <Facebook className="h-4 w-4" />
            </a>
            <a href="https://tiktok.com/@milestonepediatric" target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-brand-coral hover:text-white transition-[background-color,color] duration-300">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.9 2.9 0 1 1-2.31-2.84V9.35a6.35 6.35 0 1 0 5.76 6.32V8.69a8.21 8.21 0 0 0 4.77 1.52V6.76a4.85 4.85 0 0 1-1-.07z" />
              </svg>
            </a>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60">
          <Link to="/school-support" data-testid="footer-school-link" className="hover:text-white transition-colors duration-300">
            School & Daycare Support
          </Link>
          <Link to="/social-skills-group" data-testid="footer-social-group-link" className="hover:text-white transition-colors duration-300">
            Social Skills Group
          </Link>
          <Link to="/ages-programs" data-testid="footer-ages-link" className="hover:text-white transition-colors duration-300">
            Ages & Programs
          </Link>
          <Link to="/adult-social-skills" data-testid="footer-adult-link" className="hover:text-white transition-colors duration-300">
            Adult Evening Program
          </Link>
          <Link to="/" state={{ scrollTo: "providers" }} data-testid="footer-providers-link" className="hover:text-white transition-colors duration-300">
            Provider Referrals
          </Link>
          <Link to="/" state={{ scrollTo: "jobs" }} data-testid="footer-careers-link" className="hover:text-white transition-colors duration-300">
            Careers
          </Link>
          <span data-testid="footer-copyright">© Milestone Pediatric Therapy</span>
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white transition-colors duration-300">
            Team Login
          </Link>
        </div>
      </div>
  </footer>
);
